import React from 'react'
import {Box, Image} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    getGDIHideStyles,
    mergeGDISx,
    pickGDIDomProps,
    resolveGDIStyles,
    splitGDIResponsiveStyles
} from '../../utils/gdi-styles'
import {getExpandedFromContentArea} from '../../utils/gdi-content-area'
import GDILinkBlock from '../gdiLinkBlock'

/* Field map for GDIImageBlockStylesPerViewType → Chakra CSS props. */
const GDI_IMAGE_BLOCK_STYLE_MAP = {
    Paddings: 'padding',
    Margins: 'margin',
    MaxWidth: 'maxWidth'
}

const GDIImageBlock = ({
    HideBlockOn,
    Image: ImageMobile,
    ImageTablet,
    ImageDesktop,
    ImageAltText,
    LazyLoad,
    GDIImageBlockStylesPerViewType,
    Link,
    ...props
}) => {
    const {locale} = useIntl()
    const hideStyles = getGDIHideStyles(HideBlockOn)
    if (hideStyles.doNotLoadComponent) return null

    /* Image fields are ContentReferences — Url lives directly on the reference for media
     * assets; Expanded.Url is only populated when CMS expands a child block. */
    const resolveUrl = (ref) => ref?.Url || ref?.Expanded?.Url || null

    /* Authored image title: fr-CA locale uses TitleInFrench, all others use Title.
     * Value may live directly on the media reference or on its Expanded node. */
    const isFrCA = locale?.toLowerCase() === 'fr-ca'
    const resolveTitle = (ref) => {
        if (!ref) return null
        const source = ref.Expanded || ref
        return (isFrCA ? source.TitleInFrench : source.Title) || null
    }

    const mobileUrl = resolveUrl(ImageMobile)
    const tabletUrl = resolveUrl(ImageTablet) || mobileUrl
    const desktopUrl = resolveUrl(ImageDesktop) || tabletUrl
    const imageTitle =
        resolveTitle(ImageMobile) || resolveTitle(ImageTablet) || resolveTitle(ImageDesktop)

    if (!mobileUrl) return null

    const linkBlock = getExpandedFromContentArea(Link)

    const {styles: responsiveStyles} = resolveGDIStyles(
        GDIImageBlockStylesPerViewType,
        GDI_IMAGE_BLOCK_STYLE_MAP
    )

    const {sx: propsSx, ...restProps} = props
    const {directStyles, sxStyles: responsiveSx} = splitGDIResponsiveStyles(responsiveStyles)
    const outerSx = mergeGDISx(responsiveSx, hideStyles.sx, propsSx)

    const imageElement = (
        <Box
            data-block-type="GDIImageBlock"
            sx={outerSx}
            maxWidth="100%"
            {...directStyles}
            {...pickGDIDomProps(restProps)}
        >
            <picture>
                {desktopUrl !== tabletUrl && (
                    <source media="(min-width: 1024px)" srcSet={desktopUrl} />
                )}
                {tabletUrl !== mobileUrl && (
                    <source media="(min-width: 768px)" srcSet={tabletUrl} />
                )}
                <Image
                    src={mobileUrl}
                    alt={ImageAltText || ''}
                    {...(imageTitle ? {title: imageTitle} : {})}
                    loading={LazyLoad ? 'lazy' : 'eager'}
                    width="100%"
                    height="auto"
                    objectFit="cover"
                />
            </picture>
        </Box>
    )

    if (linkBlock) {
        return <GDILinkBlock {...linkBlock}>{imageElement}</GDILinkBlock>
    }

    return imageElement
}

GDIImageBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    Image: PropTypes.shape({Expanded: PropTypes.shape({Url: PropTypes.string})}),
    ImageTablet: PropTypes.shape({Expanded: PropTypes.shape({Url: PropTypes.string})}),
    ImageDesktop: PropTypes.shape({Expanded: PropTypes.shape({Url: PropTypes.string})}),
    ImageAltText: PropTypes.string,
    LazyLoad: PropTypes.bool,
    GDIImageBlockStylesPerViewType: PropTypes.array,
    Link: PropTypes.array,
    GDIImageBlockAudienceTargeting: PropTypes.object
}

export default GDIImageBlock
